import { Box, Checkbox, Flex, Table as KlikTable, Text } from '@lego/klik-ui';
import type { RowData } from '@tanstack/react-table';
import type { Header, Table } from './appTable';
import { getColumnFilter } from './filters/GetFilter';
import { SortIcon } from './icons/SortIcon';

const getSortIcon = <T extends RowData>(header: Header<T>) => {
  if (header.column.getIsSorted()) {
    return <SortIcon activeDirection={header.column.getIsSorted() || undefined} />;
  }
  return null;
};

type TableHeadProps<T extends RowData> = {
  enableRowSelection?: boolean;
  hasExpandableRow: boolean;
  showStickyShadow: boolean;
  table: Table<T>;
};

export const TableHead = <T extends object>({
  enableRowSelection,
  hasExpandableRow,
  showStickyShadow,
  table,
}: TableHeadProps<T>) => (
  <KlikTable.Head
    _hover={{
      '& th': { borderRightColor: 'slate.200' },
    }}
    borderBottom="1px solid"
    borderBottomColor="slate.200"
  >
    {table.getHeaderGroups().map((headerGroup) => (
      <KlikTable.Row key={headerGroup.id}>
        {enableRowSelection && (
          <KlikTable.ColumnHeader
            backgroundColor="slate.50"
            border="none"
            padding="0.6em"
            textAlign="center"
            width="2.5rem"
          >
            <Checkbox
              isChecked={table.getIsAllPageRowsSelected()}
              isDisabled={!table.getRowModel().rows.some((row) => row.getCanSelect())}
              isIndeterminate={
                table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()
              }
              onChange={table.getToggleAllPageRowsSelectedHandler()}
              size="sm"
            />
          </KlikTable.ColumnHeader>
        )}
        {hasExpandableRow && (
          <KlikTable.ColumnHeader
            backgroundColor="slate.50"
            border="none"
            padding={0}
            width="2.5rem"
          />
        )}
        {headerGroup.headers.map((header) => {
          const isSticky = header.column.columnDef.meta?.isSticky;
          return (
            <KlikTable.ColumnHeader
              backgroundColor={'slate.50'}
              border="none"
              borderRightColor={'slate.50'}
              borderRightStyle="solid"
              borderRightWidth="1px"
              fontSize={['sm', 'md']}
              height="100%"
              isTruncated={false}
              key={header.id}
              padding={0}
              position={isSticky ? 'sticky' : 'relative'}
              style={{ transition: 'border-color 0.4s' }}
              top="0"
              userSelect="none"
              width={`${header.getSize()}px`}
              {...(isSticky && {
                right: 0,
                zIndex: 2,
                boxShadow: showStickyShadow ? '-4px 0 8px -4px rgba(0, 0, 0, 0.1)' : 'none',
              })}
            >
              <Flex
                align="center"
                gap={['2', '3']}
                height="100%"
                minWidth={`${header.getSize()}px`}
                overflow="hidden"
                paddingX="1em"
                paddingY="0.6em"
                width={`${header.getSize()}px`}
              >
                <Flex
                  gap={['2', '3']}
                  onClick={
                    header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined
                  }
                >
                  <Text
                    cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                    fontWeight={header.column.getIsSorted() ? 'bold' : 'normal'}
                  >
                    <table.FlexRender header={header} />
                  </Text>
                  {getSortIcon(header)}
                </Flex>
                {getColumnFilter(header.column)}
                <Box
                  _hover={{ borderRightColor: 'slate.800' }}
                  borderRightColor="transparent"
                  borderRightStyle="solid"
                  borderRightWidth="1px"
                  css={{ transition: 'border 0.1s' }}
                  cursor="col-resize"
                  height="100%"
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  position="absolute"
                  right="-1px"
                  width="6px"
                />
              </Flex>
            </KlikTable.ColumnHeader>
          );
        })}
      </KlikTable.Row>
    ))}
  </KlikTable.Head>
);
